/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import { useI18n } from "hooks/i18n/i18n";
import React from "react";

import { useGetCurrentChapterNameForLive } from "./chapterName/useGetCurrentChapterNameForLive";
import { useGetCurrentChapterNameForVOD } from "./chapterName/useGetCurrentChapterNameForVOD";
import { getValidUrl } from "./getValidUrl";
import { generatePlaceholderImage } from "./placeholderImage";
import { useGetVideoThumbnailPresignedUrlForLive } from "./thumbnailPreview/useGetVideoThumbnailPresignedUrl";
import { useGetVideoThumbnailPresignedUrlForVOD } from "./thumbnailPreview/useGetVideoThumbnailPresignedUrlForVOD";
import { usePlayerUserActive } from "./thumbnailPreview/useUserActive";

import type { VideoData } from "./types";
import type { Cookie } from "@spwn/types/functions";
import type Player from "video.js/dist/types/player";

const thumbnailButtonStyle = css({
  "&&": {
    padding: 0,
    textAlign: "left",
    position: "relative",
    transition: "opacity 0.1s ease, transform 0.1s ease",
    "&:active": {
      opacity: 0.7,
      transform: "scale(0.97)",
    },
    ".vjs-layout-small &, .vjs-layout-x-small &": {
      paddingBottom: "20px",
    },
  },
});

const playingBadgeStyle = css({
  position: "absolute",
  top: "10px",
  right: "11px",
  backgroundColor: "white",
  color: "black",
  padding: "2px 6px",
  borderRadius: "4px",
  fontWeight: "bold",
  fontSize: "12px",
  lineHeight: "16px",
  ".vjs-layout-small &, .vjs-layout-x-small &": {
    padding: "1.5px 4px",
    top: "4px",
    right: "4px",
  },
});

const thumbnailImageStyle = css({
  width: "160px",
  height: "90px",
  display: "block",
  marginBottom: "4px",
  borderRadius: "4px",
  objectFit: "cover",
  cursor: "pointer",
  ".vjs-layout-small &, .vjs-layout-x-small &": {
    width: "128px",
    height: "72px",
  },
});

const chapterNameStyle = css({
  color: "white",
  fontSize: "12px",
  lineHeight: "16px",
  minHeight: "14px",
  ".vjs-layout-small &, .vjs-layout-x-small &": {
    fontSize: "10px",
    lineHeight: "14px",
    minHeight: "12px",
  },
  display: "inline-block",
});

type VideoThumbnailButtonProps = {
  video: VideoData;
  cookie: Cookie;
  isCurrentVideo: boolean;
  player: Player;
  handleSwitchMultiAngleVideo: (videoId: string) => void;
  eventThumbnailUrl: string;
};

export const VODThumbnailPreview: React.FC<VideoThumbnailButtonProps> = ({
  video,
  cookie,
  isCurrentVideo,
  player,
  handleSwitchMultiAngleVideo,
  eventThumbnailUrl,
}) => {
  const thumbnailUrl = useGetVideoThumbnailPresignedUrlForVOD({
    cookie,
    player,
  });

  const thumbnailUrlWithFallback =
    getValidUrl(thumbnailUrl) ??
    getValidUrl(video.thumbnailUrl) ??
    getValidUrl(eventThumbnailUrl) ??
    generatePlaceholderImage(video.name);

  const currentChapterName = useGetCurrentChapterNameForVOD({
    video,
    player,
  });

  return (
    <ThumbnailPreview
      video={video}
      isCurrentVideo={isCurrentVideo}
      thumbnailUrl={thumbnailUrlWithFallback}
      handleSwitchMultiAngleVideo={handleSwitchMultiAngleVideo}
      currentChapterName={currentChapterName}
    />
  );
};

export const LiveThumbnailPreview: React.FC<VideoThumbnailButtonProps> = ({
  video,
  cookie,
  isCurrentVideo,
  player,
  handleSwitchMultiAngleVideo,
  eventThumbnailUrl,
}) => {
  const isUserActive = usePlayerUserActive(player);

  const thumbnailUrl = useGetVideoThumbnailPresignedUrlForLive({
    cookie: { url: cookie.url, cookie: cookie.cookie },
    enablePolling: isUserActive,
  });

  const thumbnailUrlWithFallback =
    getValidUrl(thumbnailUrl) ??
    getValidUrl(video.thumbnailUrl) ??
    getValidUrl(eventThumbnailUrl) ??
    generatePlaceholderImage(video.name);

  const currentChapterName = useGetCurrentChapterNameForLive(video);

  return (
    <ThumbnailPreview
      video={video}
      isCurrentVideo={isCurrentVideo}
      thumbnailUrl={thumbnailUrlWithFallback}
      handleSwitchMultiAngleVideo={handleSwitchMultiAngleVideo}
      currentChapterName={currentChapterName}
    />
  );
};

const ThumbnailPreview: React.FC<{
  video: VideoData;
  isCurrentVideo: boolean;
  currentChapterName: string;
  thumbnailUrl: string;
  handleSwitchMultiAngleVideo: (videoId: string) => void;
}> = ({
  video,
  isCurrentVideo,
  thumbnailUrl,
  handleSwitchMultiAngleVideo,
  currentChapterName,
}) => {
  const { t } = useI18n();
  const handleClick = () => {
    if (isCurrentVideo) return;
    handleSwitchMultiAngleVideo(video.videoId);
  };

  return (
    <button
      css={thumbnailButtonStyle}
      onClick={handleClick}
      disabled={isCurrentVideo}
      aria-disabled={isCurrentVideo}
    >
      {isCurrentVideo && (
        <span css={playingBadgeStyle}>
          {t("streaming.multiAngle.nowPlaying")}
        </span>
      )}
      <img src={thumbnailUrl} alt={video.name} css={thumbnailImageStyle} />
      <span css={chapterNameStyle}>{currentChapterName}</span>
    </button>
  );
};
